import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, BaseModelObject, ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumYesNoEither } from "./enums/enumYesNoEither.generated";

// Model Objects

export class StockBatch extends ModelObject<StockBatch> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    private _dateOfBatch: LocalDate | null = null;
    /**
    * Date of Batch for batchable stock. Typically date the batch (urn) was first received. Used for FIFO.
    */
    public get dateOfBatch(): LocalDate | null { return this._dateOfBatch }
    public set dateOfBatch(value: LocalDate | null) {
      this._dateOfBatch = value === undefined ? null : value;
    }
    /**
    * Stock Item Id
    */
    public stockItemId: Guid = Guid.createEmpty();
    /**
    * URN
    * Not Nullable
    * Max Length : 1000
    */
    public urn: string = "";
    /**
    * Is batch complete
    */
    public isComplete: boolean = false;
    private _hopAlpha: number | null = null;
    /**
    * Hop Alpha value of this batch, if specified.
    * Not Negative
    */
    public get hopAlpha(): number | null { return this._hopAlpha }
    public set hopAlpha(value: number | null) {
      this._hopAlpha = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.dateOfBatch = jsonSerializedObject.dateOfBatch == null ? null : LocalDate.parse(jsonSerializedObject.dateOfBatch);
          this.stockItemId = Guid.parseOrEmpty(jsonSerializedObject.stockItemId);
          this.urn = jsonSerializedObject.urn;
          this.isComplete = jsonSerializedObject.isComplete;
          this.hopAlpha = jsonSerializedObject.hopAlpha == null ? null : jsonSerializedObject.hopAlpha;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.dateOfBatch = this.dateOfBatch == null ? null : this.dateOfBatch.toString();
        toRet.stockItemId = this.stockItemId.toString();
        toRet.urn = this.urn;
        toRet.isComplete = this.isComplete;
        toRet.hopAlpha = this.hopAlpha == null ? null : this.hopAlpha;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "StockBatch.Id cannot be empty"},
            {field: "stockItemId", rule: (v: any) => !v.isEmpty() || "StockBatch.StockItemId cannot be empty"},
            {field: "urn", rule: (v: any) => v == null || v.length <= 1000 || "Urn has too many characters (1000)"},
    ];
}


export class ItemUrn extends ModelObject<ItemUrn> {

    public itemId: Guid = Guid.createEmpty();
    public urn: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.itemId = Guid.parseOrEmpty(jsonSerializedObject.itemId);
          this.urn = jsonSerializedObject.urn;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.itemId = this.itemId.toString();
        toRet.urn = this.urn;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "itemId", rule: (v: any) => !v.isEmpty() || "ItemUrn.ItemId cannot be empty"},
    ];
}


export class StockBatchService extends ServiceBase {

  public async getStockBatchesForItems(tenantId: Guid, stockItemIds: readonly Guid[], completeOrNot: EnumYesNoEither, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<StockBatch[]> {
    const req = {
    tenantId:tenantId.toString(),
    stockItemIds:stockItemIds.map(v => v.toString()),
    completeOrNot:completeOrNot.value,
    };
    const extractor = (response:any) => response.stockBatches.map((v: any) => new StockBatch(v));;
    return this.makeJsonRequest("stockBatch/v1/getStockBatchesForItems", req, false, extractor, opts);
  };

  public async getStockBatches(tenantId: Guid, stockBatchIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<StockBatch[]> {
    const req = {
    tenantId:tenantId.toString(),
    stockBatchIds:stockBatchIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.stockBatches.map((v: any) => new StockBatch(v));;
    return this.makeJsonRequest("stockBatch/v1/getStockBatches", req, false, extractor, opts);
  };

  public async saveStockBatch(tenantId: Guid, stockBatch: StockBatch, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    stockBatch:stockBatch.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("stockBatch/v1/saveStockBatch", req, true, extractor, opts);
  };

  public async setStockBatchIsComplete(tenantId: Guid, stockBatchId: Guid, complete: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    stockBatchId:stockBatchId.toString(),
    complete:complete,
    };
    const extractor = null;
    return this.makeJsonRequest("stockBatch/v1/setStockBatchIsComplete", req, true, extractor, opts);
  };

  public async setStockBatchesIsComplete(tenantId: Guid, stockBatchIds: readonly Guid[], complete: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    stockBatchIds:stockBatchIds.map(v => v.toString()),
    complete:complete,
    };
    const extractor = null;
    return this.makeJsonRequest("stockBatch/v1/setStockBatchesIsComplete", req, true, extractor, opts);
  };

  public async getZeroNonCompletedStockBatches(tenantId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<StockBatch[]> {
    const req = {
    tenantId:tenantId.toString(),
    };
    const extractor = (response:any) => response.stockBatches.map((v: any) => new StockBatch(v));;
    return this.makeJsonRequest("stockBatch/v1/getZeroNonCompletedStockBatches", req, false, extractor, opts);
  };

  public async getStockBatchesForItemUrns(tenantId: Guid, itemUrns: readonly ItemUrn[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<StockBatch[]> {
    const req = {
    tenantId:tenantId.toString(),
    itemUrns:itemUrns.map(v => v.toJsonSerializedObject()),
    };
    const extractor = (response:any) => response.stockBatches.map((v: any) => new StockBatch(v));;
    return this.makeJsonRequest("stockBatch/v1/getStockBatchesForItemUrns", req, false, extractor, opts);
  };
}

export const stockBatchService = new StockBatchService();